import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPrice, fmtPct, changeColor } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Star, StarOff, Search } from "lucide-react";

export default function Markets() {
  const [quotes, setQuotes] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [search, setSearch] = useState("");
  const { user, refresh } = useAuth();

  const load = async () => {
    const [q, i] = await Promise.all([api.get("/commodities/quotes"), api.get("/industries")]);
    setQuotes(q.data);
    setIndustries(i.data.industries);
    setBundles(i.data.bundles);
  };
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);

  const toggle = async (code) => {
    const inList = (user.watchlist || []).includes(code);
    const next = inList ? user.watchlist.filter(c => c !== code) : [...(user.watchlist||[]), code];
    await api.put("/watchlist", { codes: next });
    toast.success(inList ? `Removed ${code}` : `Added ${code}`);
    await refresh();
  };

  const filtered = useMemo(() => {
    let out = quotes;
    if (selectedBundle) {
      const b = bundles.find(x => x.key === selectedBundle);
      if (b) out = out.filter(q => b.codes.includes(q.code));
    } else if (selectedIndustry !== "all") {
      out = out.filter(q => q.industry === selectedIndustry);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(q => q.code.toLowerCase().includes(s) || q.name.toLowerCase().includes(s));
    }
    return out;
  }, [quotes, selectedIndustry, selectedBundle, search, bundles]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Markets</div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Global agri commodities</h1>
          <p className="mt-1 text-sm text-muted-foreground">{quotes.length} commodities across {industries.length} industries. Filter by industry or use a buyer bundle.</p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="markets-search" placeholder="Search commodity or code" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      {/* Bundle chips */}
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="bundle-none"
          onClick={() => { setSelectedBundle(null); setSelectedIndustry("all"); }}
          className={`rounded-full border px-3 py-1 text-xs font-mono uppercase tracking-widest transition ${!selectedBundle && selectedIndustry==="all" ? "border-primary bg-primary/15 text-primary" : "border-border/60 text-muted-foreground hover:border-primary/40"}`}>
          All markets
        </button>
        {bundles.map(b => (
          <button
            key={b.key}
            data-testid={`bundle-${b.key}`}
            onClick={() => { setSelectedBundle(b.key); setSelectedIndustry("all"); }}
            className={`rounded-full border px-3 py-1 text-xs font-mono uppercase tracking-widest transition ${selectedBundle===b.key ? "border-primary bg-primary/15 text-primary" : "border-border/60 text-muted-foreground hover:border-primary/40"}`}>
            {b.label}
          </button>
        ))}
      </div>

      {/* Industry chips */}
      {!selectedBundle && (
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="industry-all"
            onClick={() => setSelectedIndustry("all")}
            className={`rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-widest transition ${selectedIndustry==="all" ? "border-primary text-primary" : "border-border/60 text-muted-foreground hover:border-primary/40"}`}>
            All industries
          </button>
          {industries.map(i => (
            <button
              key={i.key}
              data-testid={`industry-${i.key}`}
              onClick={() => setSelectedIndustry(i.key)}
              className={`rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-widest transition ${selectedIndustry===i.key ? "border-primary text-primary" : "border-border/60 text-muted-foreground hover:border-primary/40"}`}>
              {i.label} · {i.commodities.length}
            </button>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border/60 bg-card/60">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-background/40 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left">Commodity</th>
              <th className="px-4 py-3 text-left">Industry</th>
              <th className="px-4 py-3 text-right">Price</th>
              <th className="px-4 py-3 text-right">24h</th>
              <th className="px-4 py-3 text-right">Range</th>
              <th className="hidden px-4 py-3 text-right sm:table-cell">Feed</th>
              <th className="px-4 py-3 text-right">Watch</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {filtered.map(q => {
              const watched = (user?.watchlist || []).includes(q.code);
              return (
                <tr key={q.code} data-testid={`market-row-${q.code.toLowerCase()}`} className="transition hover:bg-secondary/30">
                  <td className="px-4 py-3">
                    <Link to={`/commodity/${q.code}`} className="font-semibold hover:text-primary">{q.name}</Link>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{q.code} · {q.unit}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="uppercase text-[10px]">{q.industry?.replace(/_/g, " ")}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right num font-semibold">{fmtPrice(q.price)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${changeColor(q.change_pct)}`}>{fmtPct(q.change_pct)}</td>
                  <td className="px-4 py-3 text-right num text-muted-foreground">{fmtPrice(q.low)} – {fmtPrice(q.high)}</td>
                  <td className="hidden px-4 py-3 text-right sm:table-cell">
                    <span className={`text-[10px] font-mono uppercase tracking-widest ${q.feed_source==="yfinance" ? "text-[hsl(var(--signal-buy))]" : "text-muted-foreground"}`}>
                      {q.feed_source === "yfinance" ? "LIVE" : "USDA"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" data-testid={`watch-toggle-${q.code.toLowerCase()}`} onClick={() => toggle(q.code)}>
                      {watched ? <Star className="h-4 w-4 fill-primary text-primary" /> : <StarOff className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">No commodities match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
